/**
 * @fileoverview Edit User State field hook.
 * @description The Edit dialog's State picker data: the catalogue's states (from the Tenants
 * prospect metadata) as options, with All India first and a retired state kept while it is the
 * user's current one, so the form never silently drops it. Also says whether the catalogue is
 * still loading or failed, so the picker is locked until the real choices are known (with only
 * All India on offer a save could silently change the user's State).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { useStateChoices } from '@/features/users/hooks/useStateChoices'

import type { UserDetail } from '@/features/users/types/users.types'

/**
 * @description Builds the State picker's options and load state for a user.
 * @param {UserDetail} user - The user being edited.
 * @returns {object} The options, and whether the catalogue is loading or failed to load.
 */
export function useEditUserStateField(user: UserDetail) {
  const { t } = useTranslation()
  const statesQuery = useStateChoices()
  const choices = statesQuery.data ?? []
  const current = user.state
  const options = [
    { value: '', label: t('sales.userManagement.editUserForm.fields.allIndia') },
    ...(current && !choices.some((choice) => choice.stateId === current.stateId)
      ? [{ value: current.stateId, label: current.name }]
      : []),
    ...choices.map((choice) => ({ value: choice.stateId, label: choice.name })),
  ]

  return { options, isLoading: statesQuery.isPending, isError: statesQuery.isError }
}
